/**
 * The ONE search schema. Every piece of navigation state in the app lives
 * here, in the URL (D01, D02): the base screen, the overlay, the sheet, and
 * the one-shot params a feature consumes once.
 *
 * Every field is optional and every field degrades to `undefined` on a bad
 * value (`.catch(undefined)`), never throws: a malformed push deep link must
 * not blank the app. Unknown params are stripped, which is how the
 * prototype's `state=` screenshot fixture disappears (a2-port-map §4).
 *
 * The URL is plain text both ways (`parseAppSearch`, `stringifyAppSearch`):
 * the router's default JSON codec writes `start: "1"` back as
 * `start=%221%22` and would parse `?q={…}` into an object. Its query decoder
 * still turns `?card=1` into the number 1 and `?ask=true` into `true`, so
 * numeric fields take a number or a digit string and text fields take any
 * primitive and hand back its text.
 *
 * `section`, `edit` and `step` stay plain strings: their feature validates
 * them, because this file must not import feature code.
 */
import { CONVERSATION_ID_PATTERN } from "@repo/contract/iam";
import { parseSearchWith, stringifySearchWith } from "@tanstack/react-router";
import { z } from "zod";

const DIGITS = /^\d+$/;
/** A report span's name: an ISO week or a calendar month (`D252`). */
const REPORT_SPAN = /^\d{4}-(W\d{2}|\d{2})$/;
const EXERCISE_ID = /^[\w.:-]+$/;
/** A client UUIDv7, as the store mints set ids (`store/uuid-v7.ts`). */
const UUID = /^[0-9a-f-]{36}$/;
/**
 * A server-minted id that goes into an API path. Production mints uuids, but
 * the contract accepts any non-empty string and every fixture uses short ids
 * (`a1`, `ps-b`), so a uuid pattern would drop them and open an empty door.
 * No `/`, `.`, `%` or whitespace, so a URL cannot splice a path segment.
 */
const ID = /^[\w-]+$/;
/** A plan share's id (`D273`): the server's own pattern. */
const SHARE_ID = /^[0-9a-z]{8,32}$/;

export const SCREENS = [
  "today",
  "workout",
  "plan",
  "progress",
  /** The person (`D218`): the profile's hub as a tab; its pages open over it. */
  "profile",
] as const;
export type Screen = (typeof SCREENS)[number];
export const DEFAULT_SCREEN: Screen = "today";

export const OVERLAYS = [
  "coach",
  "search",
  "profile",
  "wrapped",
  /** The body-photo assessment. An overlay, not a sheet: the camera needs
   *  the whole screen, and the person stands about 2 m from it (D22, T106). */
  "body",
  "diagnostics",
] as const;
export type Overlay = (typeof OVERLAYS)[number];

export const SHEETS = [
  "exercise",
  "exercises",
  "draft",
  "end",
  "catalogue",
  "summary",
  "quarantine",
  "set",
  /** The exercise picker (D181): swap a plan item, or add one to a session. */
  "swap",
  /** "Registrar treino feito": a plan session's workout, logged after it
   *  happened (`features/logbook`). */
  "logbook",
  /** "Adicionar ao Treino A": the catalogue, searched and filtered, adding
   *  to one day with a tap per exercise (`features/plan-builder`, `D197`). */
  "builder",
  /** "Organizar treinos": the plan's days in order, dragged, moved, added or
   *  deleted (`features/plan/days-sheet.tsx`, `D215`). */
  "days",
  /** "Treino rápido": a session for today from the minutes, composed by the
   *  engine and started with no plan session (`features/quick`, `D249`). */
  "quick",
  /** The weekly and monthly progress report (`features/report`, `D252`). */
  "report",
  /** "Novidades": the curated release notes (`features/community`, `D263`). */
  "news",
  /** "O que vem por aí": the curated roadmap and its votes (`D263`). */
  "roadmap",
  /** "Sugerir uma função": one feature request (`D263`). */
  "suggest",
  /** "Compartilhar ou importar plano": codes for the plan in effect, and a
   *  received code typed in (`features/plan-share`, `D273`). */
  "planshare",
  /** A shared plan's preview, `share=<id>` (`D273`). It READS; only its
   *  button imports, into a draft the review then opens (`T-145`). */
  "shared",
  /** "Convidar amigos": the person's own invite code, and a received one
   *  confirmed (`features/invite`, `D275`). */
  "invite",
  /** "Mural da comunidade": tips and ideas the owner approved, with votes
   *  and no counts (`features/community`, `D276`). */
  "board",
  /** "Galeria de planos": shared plans the owner approved; each opens the
   *  `shared` preview (`features/plan-share`, `D274`). */
  "gallery",
  /** "Personal": invitations, my trainers, my groups, templates (`D269`). */
  "coaching",
  /** One group's students and invitations (`group`, `D269`). */
  "coaching-group",
  /** One student, as their trainer sees them (`group`, `student`). */
  "coaching-student",
  /** The trainer–student conversation (`group`, `student`, `D272`). */
  "coaching-chat",
  /** "Clubes": my clubs, create, join by code, the directory
   *  (`features/clubs`, `D277`). */
  "clubs",
  /** One club (`club=<id>`): the round, the members, the chat (`D277`,
   *  `D278`). Pushed over `clubs`, it stacks (`D225`). */
  "club",
] as const;
export type Sheet = (typeof SHEETS)[number];

/**
 * The exercise sheet's tabs (`D199`): Sobre, Histórico, Recordes, Gráficos.
 * Absent is `about`. In the URL so the tab a person was reading survives a
 * reload and a shot can open one, and written with REPLACE — moving between
 * tabs is not history, so Back closes the sheet rather than walking tabs.
 */
export const EXERCISE_TABS = ["about", "history", "records", "charts"] as const;
export type ExerciseTab = (typeof EXERCISE_TABS)[number];

/**
 * A string. The router's query decoder turns a canonical number or
 * `true`/`false` into a number or boolean before any parser runs, so those
 * come back as the text they were.
 */
function text(max: number, pattern?: RegExp) {
  const base = pattern
    ? z.string().min(1).max(max).regex(pattern)
    : z.string().max(max);
  return z
    .union([z.string(), z.number(), z.boolean()])
    .transform(String)
    .pipe(base)
    .optional()
    .catch(undefined);
}

/** An integer in range, from a number or a digit-only string. */
function int(min: number, max = Number.MAX_SAFE_INTEGER) {
  return z
    .union([z.number(), z.string().regex(DIGITS).transform(Number)])
    .pipe(z.number().int().min(min).max(max))
    .optional()
    .catch(undefined);
}

export const searchSchema = z.object({
  /** The base screen. `"coach"` is accepted only to be redirected (router.tsx). */
  screen: z
    .enum([...SCREENS, "coach"])
    .optional()
    .catch(undefined),
  /** One overlay at a time. `diagnostics` is honoured only in DEV builds. */
  overlay: z.enum(OVERLAYS).optional().catch(undefined),
  sheet: z.enum(SHEETS).optional().catch(undefined),
  /** Exercise id for `sheet=exercise`. */
  ex: text(64, EXERCISE_ID),
  /** The exercise sheet's tab (`D199`); absent is "Sobre". */
  tab: z.enum(EXERCISE_TABS).optional().catch(undefined),
  /**
   * `sheet=exercise`: the exercise this one was reached from through
   * "Exercícios parecidos" (`D231`), inside the SAME sheet — a step, not a
   * new sheet. The sheet names it in its "‹ back" row; Back returns to it.
   */
  prev: text(64, EXERCISE_ID),
  /**
   * `sheet=exercise` opened from the day builder (`sheet=builder`, `D197`):
   * the sheet's footer adds the exercise to `session`, and takes it back
   * out, as the builder's rows do. Kept across the sheet's steps.
   *
   * `workout` is `sheet=catalogue` in the workout's pick mode (`D254`):
   * "Escolher" puts the exercise into today's session.
   */
  door: z.enum(["builder", "workout"]).optional().catch(undefined),
  /**
   * One-shot: start a workout through the readiness check. `?start=1` parses
   * to the number 1; both spellings normalise to `"1"`.
   */
  start: z
    .union([z.literal("1"), z.literal(1)])
    .transform(() => "1" as const)
    .optional()
    .catch(undefined),
  /** One-shot: a question already in the composer. */
  ask: text(500),
  /**
   * One-shot: a question SENT the moment the trainer opens — the plan
   * builder's "Pedir sugestões" (`D197`), whose question is the whole act.
   * Never reachable from outside: the shell's deep links pass two keys
   * (`T-145`, `T-178`).
   */
  send: text(500),
  /** Coach topic chip. */
  about: text(120),
  /** Which trainer conversation — the server's own pattern. */
  conversation: text(64, CONVERSATION_ID_PATTERN),
  /**
   * The student a trainer's conversation is about (`D270`): a SELECTOR the
   * agent Worker resolves against the caller's roster (`D74`), honoured only
   * beside `conversation`. Written by the student sheet alone; no deep link
   * carries it (the shell passes two keys, `T-145`, `T-178`).
   */
  subject: text(64, ID),
  /** Search query. */
  q: text(200),
  /** Profile section id (validated by the profile feature). */
  section: text(40),
  /** Profile editor id (validated by the profile feature). */
  edit: text(40),
  /** Retrospectiva card. Written with REPLACE while scrolling. Up to the
   *  longest story, a month's or a year's (`D204`). */
  card: int(1, 12),
  /** The Retrospectiva's span (`D204`); absent is the week's story. */
  period: z.enum(["month", "year"]).optional().catch(undefined),
  /**
   * `sheet=report` (`D252`): the report's span — absent is the week. Its
   * own param, not the Retrospectiva's `period`: that one is an OVERLAY
   * param, and closing the sheet would leave it behind.
   */
  span: z.enum(["week", "month"]).optional().catch(undefined),
  /**
   * `sheet=suggest` (`D268`): `translation` is the language card's
   * "Sugerir uma tradução" — the same request box, worded for a sentence
   * in the app's language, its text tagged with the locale.
   */
  topic: z.enum(["translation"]).optional().catch(undefined),
  /** `sheet=report`: which week (`YYYY-Www`) or month (`YYYY-MM`); absent
   *  is the last ended one, the server's clock's answer. */
  at: text(8, REPORT_SPAN),
  /** Onboarding step id (validated by the onboarding feature). */
  step: text(40),
  /** The club `sheet=club` shows — the server's id (`D277`). Opens a read
   *  and nothing else: a club is joined only by its code and a tap. */
  club: text(64, ID),
  /**
   * Body assessment id for `overlay=body` — the result view (T106, T107).
   *
   * Not `UUID`-patterned although the app mints a UUIDv7 for it: the id is the
   * SERVER's on the way back, and a pattern here would silently drop a
   * perfectly good id the moment the server chose a different shape
   * (`searchSchema` catches to `undefined` rather than throwing).
   */
  assessment: text(64),
  /**
   * Session id for `sheet=summary`; the plan session for `sheet=swap` in add
   * mode, for `sheet=draft` scrolled to one day, for `sheet=logbook`, and the
   * day `sheet=builder` adds to.
   */
  session: text(64),
  /** Logged set id for `sheet=set`. */
  set: text(64, UUID),
  /**
   * Plan item id for `sheet=swap` (the item swapped) and for `sheet=draft`
   * (the draft item the picker opens over, inside the review), and in pick
   * mode for `sheet=catalogue`.
   */
  item: text(64, ID),
  /** Muscle id: the add-mode picker's muscle, and the catalogue's filter. */
  muscle: int(1),
  /** Exercise id preselected in the picker, as the catalogue hands it back. */
  pick: text(64, EXERCISE_ID),
  /**
   * `sheet=swap` from "Não recomendar mais": the commit also excludes the
   * exercise swapped away. Only `"1"`, from either spelling, like `start`.
   */
  never: z
    .union([z.literal("1"), z.literal(1)])
    .transform(() => "1" as const)
    .optional()
    .catch(undefined),
  /**
   * One-shot, beside `start`: the plan session to start instead of today's.
   * Consumed like `start`, so a render never starts it twice.
   */
  day: text(64, ID),
  /**
   * The draft plan `sheet=draft` opens on. A review that names its draft
   * never generates one (D150, T-145).
   */
  draft: text(64, ID),
  /**
   * The shared plan `sheet=shared` previews (`D273`): the server's opaque
   * id, Crockford base 32. Opening it reads and never imports or generates;
   * never reachable from the shell's deep links, which pass two keys
   * (`T-145`, `T-178`).
   */
  share: text(32, SHARE_ID),
  /** One-shot: the protocol the draft review generates for (D150, T-145). */
  protocol: text(64),
  /** One-shot: comma-separated `excludeExerciseIds` for that generation. */
  exclude: text(2000),
  /** The group a `coaching-*` sheet is about (`D269`). Server-minted. */
  group: text(64, ID),
  /** The student a `coaching-student`/`coaching-chat` sheet is about. */
  student: text(64, ID),
  /** Read by the prefs store at boot (T12); kept so the router keeps it. */
  theme: z.enum(["light", "dark", "system"]).optional().catch(undefined),
});

export type Search = z.output<typeof searchSchema>;

/** The screen to render: `coach` and a missing value both mean Today. */
export function screenOf(search: Pick<Search, "screen">): Screen {
  const { screen } = search;
  return screen === undefined || screen === "coach" ? DEFAULT_SCREEN : screen;
}

/** Whether this search still carries the legacy `screen=coach` (router redirects it). */
export function isCoachScreen(search: Pick<Search, "screen">): boolean {
  return search.screen === "coach";
}

/** The search the `screen=coach` redirect lands on: the coach over Today. */
export function coachRedirect(search: Search): Search {
  return { ...search, overlay: "coach", screen: undefined };
}

/** Reads every search value as the literal string in the URL. */
export const parseAppSearch = parseSearchWith((value) => value);

/** Writes strings and numbers as they are; only objects would be JSON. */
export const stringifyAppSearch = stringifySearchWith(JSON.stringify);

/** `exclude` split into ids, empty entries dropped. */
export function excludedIds(search: Pick<Search, "exclude">): string[] {
  return (search.exclude ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}
